
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LessonAttendanceDialog } from './LessonAttendanceDialog';
import { formatArmenianDate } from '@/utils/dateFormatting';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from "@/hooks/use-toast";
import { Trash2, Edit2, Save, X, Plus, ChevronDown, ChevronUp, Calendar, Info } from 'lucide-react';
import { GradeCell } from './GradeCell';

interface Phase {
  id: string;
  group_id: string;
  name: string;
  phase_number: number;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  status?: string;
}

interface LessonData {
  id?: string;
  lesson_number: number;
  lesson_date?: string;
  topic?: string;
  location?: string;
  time?: string;
  lecturer_id?: string;
  marked_by_lecturer_id?: string;
  phase_id: string;
  group_id: string;
  has_attendance: boolean;
  marked_lecturer_name?: string;
  marked_lecturer_color?: string;
  status?: string; // Add status field to track completed/scheduled
}

interface PhaseGrade {
  id: string;
  student_id: string;
  grade: number;
  comment?: string;
  lecturer_id: string;
}

interface AttendanceRecord {
  lesson_id: string;
  student_id: string;
  is_present: boolean;
}

interface PhaseSectionProps {
  phase: Phase;
  students: Student[];
  onDeletePhase: (phaseId: string) => Promise<boolean>;
  onUpdatePhase: (phaseId: string, updates: Partial<Phase>) => Promise<boolean>;
  isLoading: boolean;
}

const MAX_LESSONS = 12;

// Armenian text constants using Unicode escape sequences
const TEXT = {
  LESSON: "\u0534\u0561\u057D", // Դdelays
  COMPLETED: "\u0531\u057E\u0561\u0580\u057F\u057E\u0561\u056E", // Delays
  LESSON_COMPLETED_TITLE: "\u0534\u0561\u057D\u0568 \u0561\u057E\u0561\u0580\u057F\u057E\u0561\u056E \u0567", // Delays delays է
  WAITING: "\u054D\u057A\u0561\u057D\u057E\u0578\u0582\u0574 \u0567", // Delays է
  NOT_CREATED: "\u0549\u057D\u057F\u0565\u0572\u056E\u057E\u0561\u056E", // Delays
  UNKNOWN_LECTURER: "\u0531\u0576\u0570\u0561\u0575\u057F \u0564\u0561\u057D\u0561\u056D\u0578\u057D", // Delays delays
  STUDENT: "\u0548\u0582\u057D\u0561\u0576\u0578\u0572", // Delays
  GRADE: "\u0533\u0576\u0561\u0570\u0561\u057F\u0561\u056F\u0561\u0576", // Delays
  PHASE_COMPLETED: "\u0553\u0578\u0582\u056C\u0568 \u0561\u057E\u0561\u0580\u057F\u057E\u0565\u0581 - \u0564\u0578\u0582\u0584 \u056F\u0561\u0580\u0578\u0572 \u0565\u0584 \u0563\u0576\u0561\u0570\u0561\u057F\u0561\u056F\u0561\u0576 \u057F\u0561\u056C", // Delays - Delays Delays Delays Delays
  LESSON_DETAILS: "\u0534\u0561\u057D\u056B \u0574\u0561\u0576\u0580\u0561\u0574\u0561\u057D\u0576\u0565\u0580", // Delays delays
  NO_DATE: "\u0531\u0574\u057D\u0561\u0569\u056B\u057E \u0579\u056F\u0561", // Delays delays
  NO_TOPIC: "\u0539\u0565\u0574\u0561 \u0579\u056F\u0561", // Delays delays
  NO_LOCATION: "\u054E\u0561\u0575\u0580 \u0579\u056F\u0561", // Delays delays
  NO_TIME: "\u053A\u0561\u0574 \u0579\u056F\u0561", // Delays delays
  CLICK_TO_CREATE: "\u054D\u0565\u0572\u0574\u0565\u0584 \u057D\u057F\u0565\u0572\u056E\u0565\u056C\u0578\u0582 \u0570\u0561\u0574\u0561\u0580", // Delays delays delays
  SHOW_DETAILS: "\u0544\u0561\u0576\u0580\u0561\u0574\u0561\u057D\u0576\u0565\u0580", // Delays
  HIDE_DETAILS: "\u0539\u0561\u0584\u0581\u0576\u0565\u056C", // Delays
  LESSONS_COUNT: "\u0564\u0561\u057D", // delays
  DELETE_PHASE: "\u054B\u0576\u057B\u0565\u056C", // Delays
  LOADING: "\u0532\u0565\u057C\u0576\u057E\u0578\u0582\u0574 \u0567...", // Delays է...
  ERROR: "\u054D\u056D\u0561\u056C", // Delays
  LOAD_ERROR: "\u0549\u0570\u0561\u057B\u0578\u0572\u057E\u0565\u0581 \u0562\u0565\u057C\u0576\u0565\u056C \u0564\u0561\u057D\u0565\u0580\u0568\u0589 \u053D\u0576\u0564\u0580\u0578\u0582\u0574 \u0565\u0576\u0584 \u0583\u0578\u0580\u0571\u0565\u056C \u056F\u0580\u056F\u056B\u0576\u0589" // Delays delays delays: Delays delays delays delays:
};

export const PhaseSection = ({ 
  phase, 
  students, 
  onDeletePhase, 
  onUpdatePhase, 
  isLoading 
}: PhaseSectionProps) => {
  const [lessons, setLessons] = useState<LessonData[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [phaseGrades, setPhaseGrades] = useState<PhaseGrade[]>([]);
  const [lecturerProfiles, setLecturerProfiles] = useState<{[key: string]: string}>({});
  const [lecturerColors, setLecturerColors] = useState<{[key: string]: string}>({});
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLessonDetailsExpanded, setIsLessonDetailsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(phase.name);
  const [lessonDialog, setLessonDialog] = useState<{
    isOpen: boolean;
    lesson: LessonData | null;
    studentId?: string;
    studentName?: string;
    currentAttendanceStatus?: boolean | null;
  }>({ isOpen: false, lesson: null });
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchLessons();
    fetchPhaseGrades();
  }, [phase.id]);

  useEffect(() => {
    if (lessons.length > 0) {
      fetchAttendance();
    }
  }, [lessons]);

  useEffect(() => {
    if (!user || !phase.id) return;

    let refreshTimeout: NodeJS.Timeout;

    const debouncedFetchLessons = () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      refreshTimeout = setTimeout(() => {
        fetchLessons();
      }, 300); // Reduced debounce for faster response
    };

    const channel = supabase
      .channel(`phase-lessons-${phase.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'lessons',
          filter: `phase_id=eq.${phase.id}`
        },
        async (payload) => {
          console.log('Phase lesson change:', payload);
          
          if (payload.eventType === 'INSERT') {
            // For new lessons, immediately add to local state (avoid duplicate from handleCellClick)
            const newLesson = payload.new as any;
            setLessons(prev => {
              const exists = prev.some(l => l.id === newLesson.id);
              if (!exists) {
                // Replace placeholder lesson with actual lesson data
                return prev.map(lesson => 
                  lesson.lesson_number === newLesson.lesson_number && !lesson.id
                    ? {
                        ...newLesson,
                        has_attendance: true,
                        marked_lecturer_name: lecturerProfiles[newLesson.marked_by_lecturer_id || newLesson.lecturer_id] || 'Բեռնվում է...',
                        marked_lecturer_color: lecturerColors[newLesson.marked_by_lecturer_id || newLesson.lecturer_id] || 'bg-muted'
                      }
                    : lesson
                );
              }
              return prev;
            });
          } else if (payload.eventType === 'UPDATE') {
            // For lesson updates, do immediate local update for better UX
            const updatedLesson = payload.new as any;
            setLessons(prev => prev.map(lesson => {
              if (lesson.id === updatedLesson.id) {
                return {
                  ...lesson,
                  lesson_date: updatedLesson.lesson_date,
                  topic: updatedLesson.topic,
                  location: updatedLesson.location,
                  time: updatedLesson.time,
                  status: updatedLesson.status,
                  marked_by_lecturer_id: updatedLesson.marked_by_lecturer_id
                };
              }
              return lesson;
            }));
          } else if (payload.eventType === 'DELETE') {
            const deletedLesson = payload.old as any;
            setLessons(prev => {
              const filtered = prev.filter(lesson => lesson.id !== deletedLesson.id);
              // Regenerate placeholder lessons to maintain proper numbering
              const maxLessonNumber = Math.max(...filtered.filter(l => l.id).map(l => l.lesson_number), 0);
              const updatedLessons: LessonData[] = [];
              
              for (let i = 1; i <= Math.max(MAX_LESSONS, maxLessonNumber); i++) {
                const existingLesson = filtered.find(l => l.lesson_number === i);
                if (existingLesson) {
                  updatedLessons.push(existingLesson);
                } else if (i <= MAX_LESSONS) {
                  updatedLessons.push({
                    lesson_number: i,
                    phase_id: phase.id,
                    group_id: phase.group_id,
                    has_attendance: false
                  });
                }
              }
              return updatedLessons;
            });
          }
        }
      )
      .subscribe();

    return () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      supabase.removeChannel(channel);
    };
  }, [user, phase.id]);

  // Separate effect for attendance subscription that depends on lessons
  useEffect(() => {
    if (!phase.id || lessons.length === 0) return;

    const lessonIds = lessons.filter(l => l.id).map(l => l.id!);
    if (lessonIds.length === 0) return;

    console.log('Setting up attendance subscription for lessons:', lessonIds);

    // Real-time subscription for attendance in this phase
    const attendanceChannel = supabase
      .channel(`phase-attendance-${phase.id}-${lessonIds.length}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'attendance'
        },
        (payload) => {
          console.log('Attendance change:', payload);
          
           // For attendance changes, immediately update the attendance state
           if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
             const attendanceRecord = payload.new as any;
             // Only process attendance for lessons in this phase
             if (lessonIds.includes(attendanceRecord.lesson_id)) {
               console.log('Real-time attendance update:', attendanceRecord);
               setAttendance(prev => {
                 const existing = prev.find(a => a.lesson_id === attendanceRecord.lesson_id && a.student_id === attendanceRecord.student_id);
                 if (existing) {
                   // Always update to ensure UI stays in sync with database
                   return prev.map(a => 
                     a.lesson_id === attendanceRecord.lesson_id && a.student_id === attendanceRecord.student_id
                       ? { 
                           lesson_id: attendanceRecord.lesson_id,
                           student_id: attendanceRecord.student_id,
                           is_present: attendanceRecord.is_present
                         }
                       : a
                   );
                 } else {
                   // Add new record
                   return [...prev, {
                     lesson_id: attendanceRecord.lesson_id,
                     student_id: attendanceRecord.student_id,
                     is_present: attendanceRecord.is_present
                   }];
                 }
               });
             }
           } else if (payload.eventType === 'DELETE') {
            const deletedRecord = payload.old as any;
            setAttendance(prev => prev.filter(a => 
              !(a.lesson_id === deletedRecord.lesson_id && a.student_id === deletedRecord.student_id)
            ));
          }
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up attendance subscription');
      supabase.removeChannel(attendanceChannel);
    };
  }, [phase.id, lessons]); // Dependency on lessons to recreate subscription when lessons change

  const fetchLessons = async () => {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('phase_id', phase.id)
        .order('lesson_number');

      if (error) {
        console.error('Database error fetching lessons:', error);
        throw error;
      }

      const lessonsWithAttendance = (data || []).map(lesson => ({
        ...lesson,
        group_id: phase.group_id,
        has_attendance: true
      }));

      // Get all unique lecturer IDs from lessons (both marked_by_lecturer_id and lecturer_id)
      const allLecturerIds = [
        ...lessonsWithAttendance
          .filter(lesson => lesson.marked_by_lecturer_id)
          .map(lesson => lesson.marked_by_lecturer_id)
          .filter(Boolean),
        ...lessonsWithAttendance
          .filter(lesson => lesson.lecturer_id && !lesson.marked_by_lecturer_id)
          .map(lesson => lesson.lecturer_id)
          .filter(Boolean)
      ].filter((id, index, self) => id && self.indexOf(id) === index);

      // Initialize maps for lecturer data
      const lecturerMap: {[key: string]: string} = {};
      const colorMap: {[key: string]: string} = {};

      // Fetch lecturer profiles and colors synchronously before setting lessons
      if (allLecturerIds.length > 0) {
        try {
      // Fetch lecturer profiles and colors synchronously before setting lessons
      if (allLecturerIds.length > 0) {
        try {
          // Fetch profiles
          const { data: profiles, error: profileError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', allLecturerIds);

          if (!profileError && profiles) {
            profiles.forEach(profile => {
              const firstName = profile.first_name?.trim() || '';
              const lastName = profile.last_name?.trim() || '';
              
              if (firstName && lastName) {
                lecturerMap[profile.id] = `${firstName} ${lastName}`;
              } else if (firstName) {
                lecturerMap[profile.id] = firstName;
              } else if (lastName) {
                lecturerMap[profile.id] = lastName;
              } else {
                lecturerMap[profile.id] = 'Անհայտ դասախոս';
              }
            });
          }

          // Fetch colors for all lecturers in parallel
          const colorPromises = allLecturerIds.map(async (lecturerId) => {
            try {
              const { data: colorData, error: colorError } = await supabase
                .rpc('get_lecturer_color', { lecturer_uuid: lecturerId });
              
              return {
                lecturerId,
                color: (!colorError && colorData) ? colorData : 'bg-muted'
              };
            } catch {
              return { lecturerId, color: 'bg-muted' };
            }
          });

          const colorResults = await Promise.all(colorPromises);
          colorResults.forEach(({ lecturerId, color }) => {
            colorMap[lecturerId] = color;
          });

          // Update state with fetched data
          setLecturerProfiles(prev => ({ ...prev, ...lecturerMap }));
          setLecturerColors(prev => ({ ...prev, ...colorMap }));
        } catch (error) {
          console.error('Error fetching lecturer data:', error);
          // Continue with lessons even if lecturer data fails
        }
      }
        } catch (error) {
          console.error('Error fetching lecturer data:', error);
          // Continue with lessons even if lecturer data fails
        }
      }

      // Generate placeholder lessons up to MAX_LESSONS with complete lecturer data
      const maxLessonNumber = Math.max(...lessonsWithAttendance.map(l => l.lesson_number), 0);
      const placeholderLessons: LessonData[] = [];
      
      for (let i = 1; i <= Math.max(MAX_LESSONS, maxLessonNumber); i++) {
        const existingLesson = lessonsWithAttendance.find(l => l.lesson_number === i);
        if (existingLesson) {
          const lecturerId = existingLesson.marked_by_lecturer_id || existingLesson.lecturer_id;
          placeholderLessons.push({
            ...existingLesson,
            marked_lecturer_name: lecturerId 
              ? lecturerMap[lecturerId] || 'Անհայտ դասախոս'
              : undefined,
            marked_lecturer_color: lecturerId
              ? colorMap[lecturerId] || 'bg-muted'
              : undefined
          });
        } else if (i <= MAX_LESSONS) {
          placeholderLessons.push({
            lesson_number: i,
            phase_id: phase.id,
            group_id: phase.group_id,
            has_attendance: false
          });
        }
      }

      setLessons(placeholderLessons);
    } catch (error) {
      console.error('Error fetching lessons:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել դասերը։ Խնդրում ենք փորձել կրկին։",
        variant: "destructive"
      });
      
      // Set fallback placeholder lessons in case of error
      const placeholderLessons: LessonData[] = [];
      for (let i = 1; i <= MAX_LESSONS; i++) {
        placeholderLessons.push({
          lesson_number: i,
          phase_id: phase.id,
          group_id: phase.group_id,
          has_attendance: false
        });
      }
      setLessons(placeholderLessons);
    }
  };

  // Optimized effect for updating lessons when lecturer data changes (only if lessons exist without lecturer data)
  useEffect(() => {
    if (lessons.length > 0 && Object.keys(lecturerProfiles).length > 0) {
      const needsUpdate = lessons.some(lesson => {
        const lecturerId = lesson.marked_by_lecturer_id || lesson.lecturer_id;
        return lecturerId && (!lesson.marked_lecturer_name || lesson.marked_lecturer_name === 'Բեռնվում է...');
      });

      if (needsUpdate) {
        setLessons(prev => prev.map(lesson => {
          const lecturerId = lesson.marked_by_lecturer_id || lesson.lecturer_id;
          return {
            ...lesson,
            marked_lecturer_name: lecturerId 
              ? lecturerProfiles[lecturerId] || 'Անհայտ դասախոս'
              : lesson.marked_lecturer_name,
            marked_lecturer_color: lecturerId
              ? lecturerColors[lecturerId] || 'bg-muted'
              : lesson.marked_lecturer_color
          };
        }));
      }
    }
  }, [lecturerProfiles, lecturerColors]);

  const fetchAttendance = async () => {
    const lessonIds = lessons.filter(l => l.id).map(lesson => lesson.id!);
    if (lessonIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('attendance')
        .select('lesson_id, student_id, is_present')
        .in('lesson_id', lessonIds);

      if (error) throw error;
      setAttendance(data || []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const fetchPhaseGrades = async () => {
    try {
      const { data, error } = await supabase
        .from('phase_grades')
        .select('id, student_id, grade, comment, lecturer_id')
        .eq('phase_id', phase.id);

      if (error) throw error;
      setPhaseGrades(data || []);
    } catch (error) {
      console.error('Error fetching phase grades:', error);
    }
  };

  const saveGrade = async (studentId: string, grade: number, comment?: string) => {
    if (!user) return;

    try {
      const existingGrade = phaseGrades.find(g => g.student_id === studentId);
      
      if (existingGrade) {
        const { error } = await supabase
          .from('phase_grades')
          .update({ grade, comment, updated_at: new Date().toISOString() })
          .eq('id', existingGrade.id);

        if (error) throw error;
        
        setPhaseGrades(prev => prev.map(g => 
          g.id === existingGrade.id 
            ? { ...g, grade, comment }
            : g
        ));
      } else {
        const { data, error } = await supabase
          .from('phase_grades')
          .insert([{
            phase_id: phase.id,
            student_id: studentId,
            lecturer_id: user.id,
            grade,
            comment
          }])
          .select()
          .single();

        if (error) throw error;
        
        setPhaseGrades(prev => [...prev, data]);
      }

      toast({
        title: "Հաջողված",
        description: "Գնահատականը պահպանվեց",
      });
    } catch (error) {
      console.error('Error saving grade:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց պահպանել գնահատականը",
        variant: "destructive"
      });
    }
  };

  const addNewLesson = async () => {
    const nextLessonNumber = Math.max(...lessons.map(l => l.lesson_number), 0) + 1;
    
    if (nextLessonNumber > MAX_LESSONS) {
      toast({
        title: "Սահմանափակում",
        description: `Հնարավոր է ստեղծել առավելագույնը ${MAX_LESSONS} դաս`,
        variant: "destructive"
      });
      return;
    }

    const newLesson: LessonData = {
      lesson_number: nextLessonNumber,
      phase_id: phase.id,
      group_id: phase.group_id,
      has_attendance: false
    };

    setLessonDialog({ 
      isOpen: true, 
      lesson: newLesson,
      studentId: undefined,
      studentName: undefined,
      currentAttendanceStatus: undefined
    });
  };

  const handleLessonClick = (lesson: LessonData) => {
    setLessonDialog({ 
      isOpen: true, 
      lesson: { ...lesson, group_id: phase.group_id },
      studentId: undefined,
      studentName: undefined,
      currentAttendanceStatus: undefined
    });
  };

  const handleCellClick = async (student: Student, lesson: LessonData) => {
    const isCreated = !!lesson.id;

    if (!isCreated) {
      // Do NOT auto-create the lesson with today's date.
      // Open the dialog so the lecturer explicitly chooses the lesson date.
      if (!user) {
        toast({
          title: "Սխալ",
          description: "Օգտատեր չի հայտնաբերվել",
          variant: "destructive"
        });
        return;
      }

      setLessonDialog({
        isOpen: true,
        lesson: { ...lesson, group_id: phase.group_id },
        studentId: student.id,
        studentName: `${student.first_name} ${student.last_name}`,
        currentAttendanceStatus: null,
      });
    } else {
      // Lesson exists, get current attendance status
      const currentStatus = getAttendanceStatus(student.id, lesson.id!);
      setLessonDialog({
        isOpen: true,
        lesson: { ...lesson, group_id: phase.group_id },
        studentId: student.id,
        studentName: `${student.first_name} ${student.last_name}`,
        currentAttendanceStatus: currentStatus,
      });
    }
  };


  const handleAttendanceUpdate = (lessonId: string, studentId: string, isPresent: boolean) => {
    console.log('🔄 Immediate attendance update callback called:', { lessonId, studentId, isPresent });
    
    // Force immediate UI update by creating a completely new array
    setAttendance(prev => {
      const newAttendance = [...prev];
      const existingIndex = newAttendance.findIndex(a => a.lesson_id === lessonId && a.student_id === studentId);
      
      if (existingIndex >= 0) {
        console.log('📝 Updating existing attendance record:', newAttendance[existingIndex].is_present, '->', isPresent);
        // Create new object for the updated record
        newAttendance[existingIndex] = {
          lesson_id: lessonId,
          student_id: studentId,
          is_present: isPresent
        };
        console.log('✅ Attendance state updated immediately');
      } else {
        console.log('➕ Adding new attendance record');
        newAttendance.push({
          lesson_id: lessonId,
          student_id: studentId,
          is_present: isPresent
        });
        console.log('✅ New attendance record added');
      }
      
      return newAttendance;
    });
  };

  const handleLessonSave = async () => {
    // Simply close the dialog - real-time subscription will handle all data updates
    setLessonDialog({ 
      isOpen: false, 
      lesson: null,
      studentId: undefined,
      studentName: undefined,
      currentAttendanceStatus: undefined
    });
  };

  const handleLessonDelete = async (lessonId: string) => {
    // Simply close the dialog - real-time subscription will handle all data updates
    setLessonDialog({ 
      isOpen: false, 
      lesson: null,
      studentId: undefined,
      studentName: undefined,
      currentAttendanceStatus: undefined
    });
  };

  const handleSaveEdit = async () => {
    const success = await onUpdatePhase(phase.id, { name: editedName.trim() });
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(phase.name);
    setIsEditing(false);
  };

  const getDisplayName = (fullName: string) => {
    if (!fullName || fullName.length <= 15) {
      return fullName;
    }
    
    const parts = fullName.trim().split(' ');
    if (parts.length >= 2) {
      const firstName = parts[0];
      const lastName = parts[parts.length - 1];
      return `${firstName.charAt(0)}․ ${lastName}`;
    }
    
    return fullName;
  };

  const getAttendanceStatus = (studentId: string, lessonId: string) => {
    const record = attendance.find(
      record => record.student_id === studentId && record.lesson_id === lessonId
    );
    return record ? record.is_present : null;
  };

  const getAttendanceCellClass = (status: boolean | null, isCreated: boolean) => {
    if (!isCreated) {
      return 'border-dashed border-2 border-border bg-muted/30 hover:bg-muted/40 text-muted-foreground cursor-pointer transition-all duration-200';
    }
    if (status === true) return 'bg-primary/10 hover:bg-green-500/30 border-2 border-border/70 text-muted-foreground cursor-pointer transition-all duration-200';
    if (status === false) return 'bg-destructive/10 hover:bg-red-500/30 border-2 border-destructive/40 text-destructive cursor-pointer transition-all duration-200';
    return 'bg-muted hover:bg-muted border-2 border-border text-muted-foreground cursor-pointer transition-all duration-200';
  };

  const getAttendanceCellDisplayText = (lesson: LessonData) => {
    if (lesson.lesson_date) {
      return formatArmenianDate(lesson.lesson_date);
    }
    return `Դաս ${lesson.lesson_number}`;
  };

  const getAttendanceCellIcon = (status: boolean | null, isCreated: boolean) => {
    if (!isCreated) return '➕';
    if (status === true) return '✓';
    if (status === false) return '✗';
    return '?';
  };

  const getStudentGrade = (studentId: string) => {
    return phaseGrades.find(g => g.student_id === studentId);
  };

  const isPhaseCompleted = () => {
    return lessons.filter(l => l.id).length >= 12;
  };

  const createdLessonsCount = lessons.filter(l => l.id).length;

  return (
    <>
      <Card className="bg-muted border-border/70 mb-6">
        <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-muted-foreground hover:text-muted-foreground"
                >
                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
                
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="bg-muted border-border/70 text-foreground font-mono"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit();
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveEdit}
                      disabled={isLoading}
                      className="bg-primary hover:bg-primary/90"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEdit}
                      className="text-muted-foreground border-border"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <span className="text-muted-foreground font-mono">{phase.name}</span>
                    <div className="flex items-center gap-1">
                      <Badge variant="outline" className="text-muted-foreground border-border/70">
                        Փուլ {phase.phase_number}
                      </Badge>
                      <Badge variant="outline" className="text-muted-foreground border-border/70">
                        {createdLessonsCount}/{MAX_LESSONS} դաս
                      </Badge>
                    </div>
                  </div>
                )}
              </div>

              {!isEditing && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsLessonDetailsExpanded(!isLessonDetailsExpanded)}
                    className="text-primary border-border/70 hover:bg-blue-400/10"
                  >
                    <Info className="h-4 w-4" />
                    <span className="hidden md:inline md:ml-1">Դասի մանրամասներ</span>
                    <span className="hidden md:inline">
                      {isLessonDetailsExpanded ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
                    </span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsEditing(true)}
                    className="text-primary border-border/70 hover:bg-blue-400/10"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={addNewLesson}
                    disabled={isLoading || createdLessonsCount >= MAX_LESSONS}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-mono"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden md:inline md:ml-1">Դաս ավելացնել</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDeletePhase(phase.id)}
                    disabled={isLoading}
                    className="text-destructive border-destructive/40 hover:bg-red-400/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
        </CardHeader>

        {isExpanded && (
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/70">
                    <TableHead className="text-muted-foreground font-mono sticky left-0 bg-muted z-10 min-w-[200px]">
                      Ուսանող
                    </TableHead>
                     {lessons.map((lesson) => (
                       <TableHead key={lesson.lesson_number} className="text-muted-foreground font-mono text-center min-w-[140px]">
                         <div className="flex flex-col items-center gap-1">
                            <span className="text-sm font-bold">
                              {TEXT.LESSON} {lesson.lesson_number}
                            </span>
                            {/* Show lesson status indicator */}
                            {lesson.id && lesson.status === 'completed' ? (
                              <Badge 
                                className="text-xs bg-primary/20 border-border/70 text-muted-foreground border max-w-[120px] truncate"
                                title={`${TEXT.LESSON_COMPLETED_TITLE}${lesson.marked_lecturer_name ? ` - ${lesson.marked_lecturer_name}` : ''}`}
                              >
                                ✓ {lesson.marked_lecturer_name ? getDisplayName(lesson.marked_lecturer_name) : TEXT.COMPLETED}
                             </Badge>
                           ) : lesson.id ? (
                             <Badge 
                               className={`text-xs border-2 max-w-[120px] truncate ${
                                 lesson.marked_lecturer_name?.includes(TEXT.UNKNOWN_LECTURER) 
                                   ? "bg-info/10 border-info text-info"
                                   : "bg-warning/10 border-warning text-warning"
                               }`}
                               title={lesson.marked_lecturer_name || TEXT.WAITING}
                             >
                               {getDisplayName(lesson.marked_lecturer_name || TEXT.WAITING)}
                             </Badge>
                           ) : (
                             <Badge variant="outline" className="text-xs bg-muted/50 text-muted-foreground border border-border">
                               {TEXT.NOT_CREATED}
                             </Badge>
                           )}
                         </div>
                       </TableHead>
                     ))}
                     {isPhaseCompleted() && (
                       <TableHead className="text-muted-foreground font-mono text-center min-w-[120px]">
                         <div className="flex flex-col items-center gap-1">
                           <span className="text-sm font-bold">Գնահատական</span>
                           <Badge variant="secondary" className="text-xs bg-amber-500/15 text-muted-foreground border border-amber-500/40">
                             1-10
                           </Badge>
                         </div>
                       </TableHead>
                     )}
                  </TableRow>
                  
                  {isLessonDetailsExpanded && (
                    <TableRow className="border-border/70">
                      <TableHead className="text-muted-foreground font-mono sticky left-0 bg-muted z-10 text-xs">
                        Դասի մանրամասներ
                      </TableHead>
                      {lessons.map((lesson) => (
                        <TableHead key={`details-${lesson.lesson_number}`} className="text-center min-w-[140px]">
                          <div 
                            className="space-y-1 cursor-pointer hover:bg-muted/30 p-2 rounded border border-transparent hover:border-border/70 transition-all duration-200"
                            onClick={() => handleLessonClick(lesson)}
                          >
                            <div className="text-xs text-muted-foreground">
                              {lesson.lesson_date ? formatArmenianDate(lesson.lesson_date) : 'Ամսաթիվ չկա'}
                            </div>
                            <div className="text-xs text-foreground">
                              {lesson.topic || 'Թեմա չկա'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {lesson.location || 'Վայր չկա'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {lesson.time || 'Ժամ չկա'}
                            </div>
                            {!lesson.id && (
                              <div className="text-xs text-primary">
                                Սեղմեք ստեղծելու համար
                              </div>
                            )}
                           </div>
                         </TableHead>
                       ))}
                       {isPhaseCompleted() && (
                         <TableHead className="text-center min-w-[120px]">
                           <div className="text-xs text-muted-foreground p-2">
                             Փուլը ավարտվեց - դուք կարող եք գնահատական տալ
                           </div>
                         </TableHead>
                       )}
                     </TableRow>
                   )}
                 </TableHeader>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student.id} className="border-border/70">
                       <TableCell className="text-foreground font-mono font-medium sticky left-0 bg-muted z-10">
                         <div className="flex items-center gap-2">
                           <span>{student.first_name} {student.last_name}</span>
                           {student.status && student.status !== 'active' && (
                             <Badge 
                               variant={
                                 student.status === 'completed' ? 'default' :
                                 student.status === 'expelled' ? 'destructive' :
                                 'secondary'
                               } 
                               className="text-xs font-mono"
                             >
                               {student.status === 'completed' ? 'Ավարտած' :
                                student.status === 'expelled' ? 'Հեռացված' :
                                student.status === 'suspended' ? 'Կասեցված' : student.status}
                             </Badge>
                           )}
                         </div>
                       </TableCell>
                      {lessons.map((lesson) => {
                        const isCreated = !!lesson.id;
                        const status = isCreated ? getAttendanceStatus(student.id, lesson.id!) : null;
                        
                        return (
                          <TableCell key={lesson.lesson_number} className="p-1 text-center">
                            <button
                              onClick={() => handleCellClick(student, lesson)}
                              className={`w-full h-16 rounded-lg font-mono text-sm flex flex-col items-center justify-center gap-1 ${getAttendanceCellClass(status, isCreated)}`}
                            >
                              <span className="text-lg font-bold">
                                {getAttendanceCellIcon(status, isCreated)}
                              </span>
                              <span className="text-xs font-medium">
                                {getAttendanceCellDisplayText(lesson)}
                              </span>
                            </button>
                           </TableCell>
                         );
                       })}
                       {isPhaseCompleted() && (
                         <TableCell className="p-2 text-center">
                           <GradeCell
                             student={student}
                             grade={getStudentGrade(student.id)}
                             onSaveGrade={saveGrade}
                           />
                         </TableCell>
                       )}
                     </TableRow>
                   ))}
                 </TableBody>
              </Table>
            </div>
          </CardContent>
        )}
      </Card>

      <LessonAttendanceDialog
        isOpen={lessonDialog.isOpen}
        onClose={() => setLessonDialog({ 
          isOpen: false, 
          lesson: null,
          studentId: undefined,
          studentName: undefined,
          currentAttendanceStatus: undefined
        })}
        lesson={lessonDialog.lesson}
        studentId={lessonDialog.studentId}
        studentName={lessonDialog.studentName}
        currentAttendanceStatus={lessonDialog.currentAttendanceStatus}
          onSave={handleLessonSave}
          onDelete={handleLessonDelete}
          students={students}
          onAttendanceUpdate={handleAttendanceUpdate}
      />
    </>
  );
};
